// frontend/src/pages/NestPage.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { addDays, startOfWeek, format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, LayoutGrid, CalendarDays } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { cellForDate, placeActivities } from '@/lib/nestTimeline';
import { assignChildColor } from '@/lib/childColors';
import DayTimeline from '@/components/nest/DayTimeline';
import WeekTimeline from '@/components/nest/WeekTimeline';
import ActivityPopover from '@/components/nest/ActivityPopover';
import LessonPopover from '@/components/nest/LessonPopover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_START = '08:00';
const DAY_END = '17:00';

export default function NestPage() {
  const { user, authHeaders } = useAuth();
  const children = user?.children || [];
  const [viewMode, setViewMode] = useState('day'); // 'day' | 'week'
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [lessons, setLessons] = useState([]);
  const [perChild, setPerChild] = useState({}); // name -> {planner, progress}
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popoverTarget, setPopoverTarget] = useState(null); // {childName, date, editing: activity|null} | null
  const [lessonPopoverTarget, setLessonPopoverTarget] = useState(null); // the clicked lesson item | null

  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchorDate, { weekStartsOn: 1 }), i));
  const rangeStart = format(viewMode === 'day' ? anchorDate : weekDates[0], 'yyyy-MM-dd');
  const rangeEnd = format(viewMode === 'day' ? anchorDate : weekDates[6], 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const lessonsRes = await axios.get(`${API}/lessons`);
      setLessons(lessonsRes.data);

      const perChildResults = await Promise.all(
        children.map(async c => {
          const [plannerRes, progressRes] = await Promise.all([
            axios.get(`${API}/planner?child=${encodeURIComponent(c.name)}`, { headers: authHeaders }).catch(() => ({ data: null })),
            axios.get(`${API}/progress?child=${encodeURIComponent(c.name)}`, { headers: authHeaders }).catch(() => ({ data: [] })),
          ]);
          return [c.name, { planner: plannerRes.data, progress: progressRes.data || [] }];
        })
      );
      setPerChild(Object.fromEntries(perChildResults));

      const activitiesRes = await axios.get(`${API}/custom-activities?start=${rangeStart}&end=${rangeEnd}`, { headers: authHeaders });
      setActivities(activitiesRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, rangeStart, rangeEnd]);

  useEffect(() => { load(); }, [load]);

  // Build the placed (scheduled) items for one child on one specific date.
  const itemsForChildOnDate = (childName, date) => {
    const iso = format(date, 'yyyy-MM-dd');
    const { planner, progress } = perChild[childName] || {};
    const completedRefs = new Set((progress || []).filter(p => p.completed).map(p => p.lesson_reference));
    const cell = planner ? cellForDate(planner, date) : null;
    const slots = cell ? (planner.slots || []).filter(s => s.week === cell.week && s.day === cell.day) : [];

    const lessonItems = slots.map(s => {
      const lesson = lessons.find(l => l.reference === s.lesson_reference);
      return {
        id: s.id,
        title: lesson?.title || s.lesson_reference,
        duration_minutes: lesson?.duration || 30,
        start_time: s.display_time || undefined,
        kind: 'lesson',
        completed: completedRefs.has(s.lesson_reference),
      };
    });

    const customItems = activities
      .filter(a => a.date === iso && a.child_names.includes(childName))
      .map(a => ({ id: a.id, title: a.title, duration_minutes: a.duration_minutes, start_time: a.start_time, kind: 'custom', color: a.color, notes: a.notes, _raw: a }));

    const fixedItems = [...lessonItems.filter(i => i.start_time), ...customItems];
    const flexibleItems = lessonItems.filter(i => !i.start_time);

    return placeActivities({ fixedItems, flexibleItems, dayStartTime: DAY_START });
  };

  const dayChildrenData = children.map(c => ({
    name: c.name,
    color: assignChildColor(c.name),
    items: itemsForChildOnDate(c.name, anchorDate),
  }));

  const weekChildrenData = children.map(c => ({
    name: c.name,
    color: assignChildColor(c.name),
    itemsByDate: Object.fromEntries(weekDates.map(d => [format(d, 'yyyy-MM-dd'), itemsForChildOnDate(c.name, d)])),
  }));

  const openAddPopover = (childName, dateIso) => setPopoverTarget({ childName, date: dateIso || format(anchorDate, 'yyyy-MM-dd'), editing: null });
  const openEditPopover = (item) => {
    if (item.kind === 'lesson') { setLessonPopoverTarget(item); return; }
    setPopoverTarget({ childName: null, date: item._raw.date, editing: item._raw });
  };

  const handleSave = async (values) => {
    try {
      if (popoverTarget.editing) {
        await axios.put(`${API}/custom-activities/${popoverTarget.editing.id}`, values, { headers: authHeaders });
        toast.success('Activity updated');
      } else {
        await axios.post(`${API}/custom-activities`, values, { headers: authHeaders });
        toast.success('Activity added');
      }
      setPopoverTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save activity');
      throw e;
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/custom-activities/${popoverTarget.editing.id}`, { headers: authHeaders });
      toast.success('Activity removed');
      setPopoverTarget(null);
      load();
    } catch (e) {
      toast.error('Could not remove activity');
      throw e;
    }
  };

  // Both write straight to the planner_slots row the Planner page reads —
  // moving or removing a lesson here updates the Planner too, same data.
  const handleLessonTimeSave = async (newTime) => {
    try {
      await axios.put(`${API}/planner/slots/${lessonPopoverTarget.id}`, { display_time: newTime }, { headers: authHeaders });
      toast.success('Lesson time updated');
      setLessonPopoverTarget(null);
      load();
    } catch (e) {
      toast.error('Could not update lesson time');
      throw e;
    }
  };

  const handleLessonRemove = async () => {
    try {
      await axios.delete(`${API}/planner/slots/${lessonPopoverTarget.id}`, { headers: authHeaders });
      toast.success('Lesson removed from this day');
      setLessonPopoverTarget(null);
      load();
    } catch (e) {
      toast.error('Could not remove lesson');
      throw e;
    }
  };

  // Drag-to-reschedule (Day view) — same two endpoints as the popovers' manual
  // time field, just triggered by a drop instead of a form submit. Updates
  // state directly instead of calling load(), so there's no full-page
  // "Loading…" flash between letting go and seeing the new position — that
  // full reload is still there as a fallback if the save actually fails.
  const handleMoveItem = async (item, newTime) => {
    if (item.kind === 'lesson') {
      setPerChild(prev => {
        for (const name of Object.keys(prev)) {
          const slots = prev[name]?.planner?.slots;
          if (!slots?.some(s => s.id === item.id)) continue;
          return {
            ...prev,
            [name]: {
              ...prev[name],
              planner: {
                ...prev[name].planner,
                slots: slots.map(s => s.id === item.id ? { ...s, display_time: newTime } : s),
              },
            },
          };
        }
        return prev;
      });
    } else {
      setActivities(prev => prev.map(a => a.id === item.id ? { ...a, start_time: newTime } : a));
    }

    try {
      if (item.kind === 'lesson') {
        await axios.put(`${API}/planner/slots/${item.id}`, { display_time: newTime }, { headers: authHeaders });
      } else {
        await axios.put(`${API}/custom-activities/${item.id}`, { start_time: newTime }, { headers: authHeaders });
      }
    } catch (e) {
      toast.error('Could not move — try again');
      load(); // the optimistic update above is now wrong — reconcile with the server
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-sand flex items-center justify-center text-slate-400">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-sand">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center"><BrandMark size={36} /></Link>
          <h1 className="font-heading text-lg font-semibold text-fern-dark">The Nest</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setAnchorDate(d => addDays(d, viewMode === 'day' ? -1 : -7))} data-testid="nest-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="font-heading font-semibold text-fern-dark min-w-[180px] text-center" data-testid="nest-date-label">
              {viewMode === 'day' ? format(anchorDate, 'EEEE, d MMMM') : `${format(weekDates[0], 'd MMM')} – ${format(weekDates[6], 'd MMM')}`}
            </div>
            <Button variant="outline" size="icon" onClick={() => setAnchorDate(d => addDays(d, viewMode === 'day' ? 1 : 7))} data-testid="nest-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-1 bg-white rounded-full p-1 border border-sand-darker">
            <Button variant={viewMode === 'day' ? 'default' : 'ghost'} size="sm" className={viewMode === 'day' ? 'bg-fern text-white rounded-full' : 'rounded-full'} onClick={() => setViewMode('day')} data-testid="nest-day-view-btn">
              <CalendarDays className="w-4 h-4 mr-1.5" /> Day
            </Button>
            <Button variant={viewMode === 'week' ? 'default' : 'ghost'} size="sm" className={viewMode === 'week' ? 'bg-fern text-white rounded-full' : 'rounded-full'} onClick={() => setViewMode('week')} data-testid="nest-week-view-btn">
              <LayoutGrid className="w-4 h-4 mr-1.5" /> Week
            </Button>
          </div>
        </div>

        {children.length === 0 && (
          <p className="text-slate-500">Add children on the Family page to start using The Nest.</p>
        )}

        {children.length > 0 && viewMode === 'day' && (
          <DayTimeline childrenData={dayChildrenData} dayStartTime={DAY_START} dayEndTime={DAY_END} onAddActivity={openAddPopover} onEditItem={openEditPopover} onMoveItem={handleMoveItem} />
        )}
        {children.length > 0 && viewMode === 'week' && (
          <WeekTimeline weekDates={weekDates} childrenData={weekChildrenData} onAddActivity={(name, iso) => openAddPopover(name, iso)} onEditItem={openEditPopover} />
        )}

        {popoverTarget && (
          <ActivityPopover
            key={popoverTarget.editing?.id ?? `${popoverTarget.childName}-${popoverTarget.date}`}
            open={!!popoverTarget}
            onOpenChange={(next) => { if (!next) setPopoverTarget(null); }}
            initialValues={popoverTarget.editing}
            initialChildNames={popoverTarget.childName ? [popoverTarget.childName] : undefined}
            familyChildren={children}
            defaultDate={popoverTarget.date}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        )}

        {lessonPopoverTarget && (
          <LessonPopover
            key={lessonPopoverTarget.id}
            open={!!lessonPopoverTarget}
            onOpenChange={(next) => { if (!next) setLessonPopoverTarget(null); }}
            lesson={lessonPopoverTarget}
            onSave={handleLessonTimeSave}
            onRemove={handleLessonRemove}
          />
        )}
      </main>
    </div>
  );
}
